import { IClinic, IDoctor, IRedeemCoupon } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Input,
  Form,
  Row,
  Col,
  Select,
  FormInstance,
  Typography,
  Button,
  Space,
  Modal,
} from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { doctorComments, enumStatus, userRoles } from "@configs";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth, setLoading, updateProgram } from "@redux";
import { clinicApi } from "@api";
import { useNotify } from "@utils";
import { CameraIcon, SharedButton, SharedIconButton } from "@components";
import { ComponentReader } from "./Reader";
import styled from "styled-components";
import { ClientInformation } from "./ClientInfo";
const { Link } = Typography;

const { confirm } = Modal;
interface IProps {
  coupon?: IRedeemCoupon;
}

const initValue: IRedeemCoupon = {
  couponCode: "",
  doctorName: "",
  doctorComment: "",
};

export const RedeemCdicForm = memo(({ coupon }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupRedeem } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();
  const auth = useSelector(selectAuth)?.auth;
  const [data, setData] = useState<Partial<IRedeemCoupon>>(initValue);
  const [isScan, setIsScan] = useState<boolean>(false);
  const [button, setButton] = useState<string>("");
  const [client, setClient] = useState<IClinic>();
  const [serviceCategory, setServiceCategory] = useState<string>("");  
  const [programId, setProgramId] = useState<string>("");
  const [doctor, setDoctor] = useState<IDoctor[]>([]);
  const redeemSchema = Yup.object().shape(YupRedeem);
  const [redeemForm] = Form.useForm();

  useEffect(() => {
    fetchAdmin();
  }, [auth]);

  useEffect(() => {
    if (coupon) {
      setData({ ...data, ...coupon });
      formRef.current?.setFieldsValue(coupon);
    }
  }, [coupon]);

  const fetchAdmin = async () => {
    try {
      dispatch(setLoading(true));
      const res = await clinicApi.clinic(auth?.userId || "");
      setDoctor(res?.data?.doctors);
      dispatch(updateProgram(res?.data?.joinedProgram));
    } catch (err: any) {
      error(t("message.create.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRedeem = async (values: IRedeemCoupon) => {
    try {
      dispatch(setLoading(true));
      const res = await clinicApi.redeemCdicCoupon({ ...values, doctorId: values.doctorName, doctorComment: values?.doctorComment || "", programId });
      success(res?.data?.message);
      redeemForm.resetFields();
      setClient(undefined);
      setServiceCategory("");
      setProgramId("");
    } catch (err: any) {
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const onFinish = (values: IRedeemCoupon) => {
    if (button === "reject" && !values?.doctorComment) {
      error(t("page.need_doctor_comment_to_reject"));
    } else {
      confirm({
        title: t("page.confirmation"),
        content:
          button === "reject" ? t("page.continue_the_process") : t("page.redeem_the_process"),
        onOk() {
          handleRedeem({
            ...values,
            status: button === "redeem" ? enumStatus.REDEEMED : enumStatus.REJECT,
          });
        },
        onCancel() {
          console.log("cancel");
        },
      });
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await redeemSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const handleScanModal = () => setIsScan(!isScan);
  const handleScanQr = (value: string) => {
    redeemForm.setFieldsValue({ couponCode: value });
    handleScanModal();
  };
  const selectedState: string = Form.useWatch("couponCode", redeemForm);
  const doctorComment: string = Form.useWatch("doctorComment", redeemForm);

  useEffect(() => {
    if (selectedState && selectedState.length === 8) {
      scanCode();
    }
  }, [selectedState]);
  const scanCode = async () => {
    try {
      dispatch(setLoading(true));
      const res = await clinicApi.scanCdicCode({ couponCode: selectedState });
      setClient(res?.data?.coupon?.data?.client);
      setServiceCategory(res?.data?.coupon?.data?.serviceCategory);
      setProgramId(res?.data?.coupon?.data?.programId);
      success(res?.data?.message);
    } catch (err: any) {
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const handleClick = (param: string) => {
    setButton(param);
  };
  return (
    <>
      <Form
        form={redeemForm}
        name="basic"
        ref={formRef}
        initialValues={data}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
      >
        <Row gutter={{ md: 0, lg: 0 }}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.coupon_code")} name="couponCode" rules={[yupSync]}>
              <Row gutter={{ md: 0, lg: 0 }}>
                <Col lg={11} md={11} sm={11} xs={24}>
                  <Input value={selectedState} />
                </Col>
                <Col span={2}>
                  <Button type="link">{t("page.or")}</Button>
                </Col>
                <Col lg={11} md={11} sm={11} xs={24}>
                  <Space direction="horizontal">
                    <SharedIconButton
                      onClick={handleScanModal}
                      shape="circle"
                      icon={<CameraIcon />}
                    />
                    <Link onClick={handleScanModal}>{t("page.scan_coupon_code")}</Link>
                  </Space>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.doctor_name")} name="doctorName" rules={[yupSync]}>
              <Select placeholder={t("page.selected_doctor_name")}>
                {doctor && doctor.length > 0 && doctor.map((item) => (
                  <Select.Option key={item?.userId} value={item?._id}>
                    {item?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.doctor_comment")} name="doctorComment">
              <Input.TextArea placeholder={t("page.selected_comment")} rows={2} allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <StyledFormAction>
            <Form.Item name="reject">
              <SharedButton
                type="default"
                htmlType="submit"
                onClick={() => handleClick("reject")}
                disabled={!doctorComment}
              >
                {t("page.reject")}
              </SharedButton>
            </Form.Item>
            <Form.Item name="redeem">
              <SharedButton
                type="primary"
                htmlType="submit"
                size="middle"
                onClick={() => handleClick("redeem")}
                disabled={!client}
              >
                {t("page.redeem")}
              </SharedButton>
            </Form.Item>
          </StyledFormAction>
        </Form.Item>
      </Form>

      {isScan && (
        <ComponentReader open={isScan} handleModal={handleScanModal} callback={handleScanQr} />
      )}
      {client && <ClientInformation data={client} serviceCategory={serviceCategory} />}
    </>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
